import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import moment from "moment";
import api from "../../../../api/api";
import HistoryDateFilter from "./HistoryDateFilter";

const UserContactMessagesModal = ({ show, onClose, user, t }) => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState(moment().subtract(6, 'days'));
    const [endDate, setEndDate] = useState(moment());
    const [searchText, setSearchText] = useState("");
    const [searchMode, setSearchMode] = useState("date");
    const [count, setCount] = useState(20);
    const [statusFilter, setStatusFilter] = useState("all"); // "all", "read", "unread"
    const [replies, setReplies] = useState({});
    const [loadingReplies, setLoadingReplies] = useState({});
    const [expandedRepliesIds, setExpandedRepliesIds] = useState({});

    const fetchData = (currentMode = searchMode, start = startDate, end = endDate, cnt = count, status = statusFilter) => {
        if (!user) return;
        setLoading(true);

        let isReadParam = "";
        if (status === "read") {
            isReadParam = "&isRead=true";
        } else if (status === "unread") {
            isReadParam = "&isRead=false";
        }

        let url = "";
        if (currentMode === "date") {
            url = `/ContactUs/?userId=${user.Id}&startDate=${start.format('DD/MM/YYYY')}&endDate=${end.format('DD/MM/YYYY')}&count=1000&searchMode=date${isReadParam}`;
        } else {
            url = `/ContactUs/?userId=${user.Id}&count=${cnt}&searchMode=count${isReadParam}`;
        }

        api.get(url)
            .then(resp => {
                setMessages(resp.data?.data || []);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    };

    const toggleReplies = (msgId) => {
        if (expandedRepliesIds[msgId]) {
            setExpandedRepliesIds(prev => ({ ...prev, [msgId]: false }));
            return;
        }

        setExpandedRepliesIds(prev => ({ ...prev, [msgId]: true }));

        if (!replies[msgId] && !loadingReplies[msgId]) {
            setLoadingReplies(prev => ({ ...prev, [msgId]: true }));
            api.get(`/ContactUs/${msgId}`)
                .then(resp => {
                    setReplies(prev => ({ ...prev, [msgId]: resp.data?.data || [] }));
                    setLoadingReplies(prev => ({ ...prev, [msgId]: false }));
                })
                .catch(() => {
                    setLoadingReplies(prev => ({ ...prev, [msgId]: false }));
                });
        }
    };

    useEffect(() => {
        if (show && user) {
            fetchData(searchMode, startDate, endDate, count, statusFilter);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, user, statusFilter]);

    const filteredMessages = messages.filter(msg => {
        if (searchMode === "date") {
            const msgDate = moment(msg.CreationDate, "YYYY-MM-DD HH:mm");
            const isWithinDate = msgDate.isBetween(startDate.clone().startOf('day'), endDate.clone().endOf('day'), null, '[]');
            if (!isWithinDate) return false;
        }

        if (!searchText) return true;
        const text = searchText.toLowerCase();
        return (
            (msg.Title && msg.Title.toLowerCase().includes(text)) ||
            (msg.Details && msg.Details.toLowerCase().includes(text)) ||
            (msg.Sender && msg.Sender.toLowerCase().includes(text)) ||
            (msg.Email && msg.Email.toLowerCase().includes(text))
        );
    });

    return (
        <Modal size="lg" centered show={show} onHide={onClose} className="admin-history-modal" enforceFocus={false}>
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="w-100">
                    <div className="d-flex align-items-center">
                        <div className="history-modal-icon-wrapper ml-3 text-info">
                            <i className="fad fa-envelope"></i>
                        </div>
                        <div className="text-right">
                            <div className="h5 mb-1 font-weight-bold text-dark">{t("Pages.UsersControl.action.contactMessages") || "رسائل اتصل بنا"}</div>
                            <div className="small text-muted">{user?.Name}</div>
                        </div>
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-4 pb-4">
                <HistoryDateFilter
                    startDate={startDate}
                    endDate={endDate}
                    searchMode={searchMode}
                    count={count}
                    searchText={searchText}
                    showSearchModeSelect={true}
                    onApply={(mode, start, end, cnt, search) => {
                        setSearchMode(mode);
                        setStartDate(start);
                        setEndDate(end);
                        setCount(cnt);
                        setSearchText(search);
                        fetchData(mode, start, end, cnt, statusFilter);
                    }}
                    t={t}
                />

                {/* Status filter switcher */}
                <div className="d-flex justify-content-center mb-4">
                    <div className="btn-group btn-group-toggle" style={{ direction: 'rtl', borderRadius: '12px', overflow: 'hidden', border: '1px solid #cbd5e1' }}>
                        <button
                            type="button"
                            className="btn px-4 py-2"
                            style={{
                                fontSize: '0.85rem',
                                fontWeight: '600',
                                backgroundColor: statusFilter === "all" ? "#92278f" : "#ffffff",
                                color: statusFilter === "all" ? "#ffffff" : "#64748b",
                                border: 'none',
                                outline: 'none',
                                transition: 'all 0.2s'
                            }}
                            onClick={() => setStatusFilter("all")}
                        >
                            {t("Pages.UsersControl.contactMessages.All") || "الكل"}
                        </button>
                        <button
                            type="button"
                            className="btn px-4 py-2"
                            style={{
                                fontSize: '0.85rem',
                                fontWeight: '600',
                                backgroundColor: statusFilter === "unread" ? "#ea580c" : "#ffffff",
                                color: statusFilter === "unread" ? "#ffffff" : "#64748b",
                                border: 'none',
                                borderRight: '1px solid #cbd5e1',
                                borderLeft: '1px solid #cbd5e1',
                                outline: 'none',
                                transition: 'all 0.2s'
                            }}
                            onClick={() => setStatusFilter("unread")}
                        >
                            {t("Pages.UsersControl.contactMessages.Unread") || "الغير مقروءة"}
                        </button>
                        <button
                            type="button"
                            className="btn px-4 py-2"
                            style={{
                                fontSize: '0.85rem',
                                fontWeight: '600',
                                backgroundColor: statusFilter === "read" ? "#16a34a" : "#ffffff",
                                color: statusFilter === "read" ? "#ffffff" : "#64748b",
                                border: 'none',
                                outline: 'none',
                                transition: 'all 0.2s'
                            }}
                            onClick={() => setStatusFilter("read")}
                        >
                            {t("Pages.UsersControl.contactMessages.Read") || "المقروءة"}
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <i className="fad fa-sync fa-spin fa-2x text-muted mb-2"></i>
                        <div className="text-muted small">{t("Pages.UsersControl.Loading") || "جاري التحميل..."}</div>
                    </div>
                ) : filteredMessages.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                        <i className="fal fa-file-search fa-2x mb-2 d-block"></i>
                        <span>{t("Pages.UsersControl.NotFound") || "لا توجد نتائج"}</span>
                    </div>
                ) : (
                    <div className="history-modal-list scrollbar-custom mx-0" style={{ direction: 'rtl' }}>
                        {filteredMessages.map((msg) => (
                            <div key={msg.Id} className="w-100 mb-3 compact-history-card">
                                <div className="compact-history-row align-items-start">
                                    <div className="compact-history-left w-100 flex-column align-items-start">
                                        <div className="d-flex align-items-center justify-content-between w-100 mb-2">
                                            <div className="d-flex align-items-center gap-2">
                                                <div className="compact-history-icon-box" style={{ background: '#e0f2fe', color: '#0284c7' }}>
                                                    <i className="fad fa-envelope-open-text"></i>
                                                </div>
                                                <h6 className="compact-history-title mb-0 font-weight-bold text-dark">{msg.Title}</h6>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                <span className={`compact-history-badge ${msg.IsRead ? "green" : "amber"}`}>
                                                    <i className={`fal ${msg.IsRead ? "fa-envelope-open" : "fa-envelope"} ml-1`}></i>
                                                    {msg.IsRead ? t("Pages.UsersControl.contactMessages.Read") : t("Pages.UsersControl.contactMessages.Unread")}
                                                </span>
                                                {msg.RepliesCount > 0 && (
                                                    <span className="compact-history-badge blue">
                                                        <i className="fal fa-reply ml-1"></i>
                                                        {t("Pages.UsersControl.contactMessages.Replies")}: {msg.RepliesCount}
                                                    </span>
                                                )}
                                                {msg.RepliesCount > 0 && (
                                                    <button
                                                        type="button"
                                                        className="compact-history-toggle-btn"
                                                        onClick={() => toggleReplies(msg.Id)}
                                                        title={t("Pages.UsersControl.contactMessages.ShowReplies") || "عرض الردود"}
                                                    >
                                                        <i className={`fas fa-chevron-${expandedRepliesIds[msg.Id] ? 'up' : 'down'}`}></i>
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        <div className="w-100 pr-3 pl-3 py-2 mb-2 bg-light rounded text-right" style={{ borderRight: '3px solid #cbd5e1' }}>
                                            <div className="small text-muted font-weight-bold mb-1">{t("Pages.UsersControl.contactMessages.Details")}:</div>
                                            <p className="mb-0 text-dark" style={{ whiteSpace: 'pre-line', fontSize: '0.9rem', lineHeight: '1.5' }}>
                                                {msg.Details}
                                            </p>
                                        </div>

                                        {/* Collapsible Replies */}
                                        {msg.RepliesCount > 0 && (
                                            <div className={`compact-history-collapsible ${expandedRepliesIds[msg.Id] ? 'show' : ''}`} style={{ width: '100%' }}>
                                                <div className="w-100 pr-3 pl-3 py-2 mb-2 rounded text-right mt-2" style={{ background: '#f0fdf4', borderRight: '3px solid #16a34a' }}>
                                                    <div className="small text-success font-weight-bold mb-2">
                                                        <i className="fal fa-comments ml-1"></i>
                                                        {t("Pages.MailBox.RepliesList") || "الردود"}:
                                                    </div>
                                                    {loadingReplies[msg.Id] ? (
                                                        <div className="text-center py-2">
                                                            <i className="fad fa-sync fa-spin text-muted"></i>
                                                        </div>
                                                    ) : !replies[msg.Id] || replies[msg.Id].length === 0 ? (
                                                        <div className="text-muted small">{t("Pages.MailBox.NoReplies") || "لا توجد ردود"}</div>
                                                    ) : (
                                                        <div className="d-flex flex-column gap-2">
                                                            {replies[msg.Id].map(reply => (
                                                                <div key={reply.Id} className="border-bottom pb-2 mb-2" style={{ borderBottom: '1px solid #d1fae5' }}>
                                                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                                                        <span className="font-weight-bold text-dark small">{reply.User?.Name}</span>
                                                                        <span className="text-muted small" style={{ fontSize: '0.8rem' }}>{reply.CreationDate}</span>
                                                                    </div>
                                                                    <div className="text-dark small" style={{ fontSize: '0.85rem', lineHeight: '1.4' }} dangerouslySetInnerHTML={{ __html: reply.Body }} />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        <div className="d-flex justify-content-between align-items-center w-100 text-muted small mt-2">
                                            <div className="d-flex flex-wrap gap-3">
                                                {msg.Email && (
                                                    <span>
                                                        <i className="fal fa-at ml-1 text-info"></i>
                                                        {msg.Email}
                                                    </span>
                                                )}
                                            </div>
                                            <span className="badge badge-light p-2">
                                                <i className="fal fa-calendar-alt ml-1"></i>
                                                {msg.CreationDate}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0 text-center justify-content-center">
                <button type="button" className="btn btn-light px-5 py-2" style={{ borderRadius: '10px', fontWeight: '600' }} onClick={onClose}>
                    {t("Close") || "إغلاق"}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default UserContactMessagesModal;
